import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDocs, query, increment, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const UnshippedProductionActions = ({ item, userData, recipes }) => {
    const [loading, setLoading] = useState(false);
    const [activeOrders, setActiveOrders] = useState([]);
    const [selectedRecipeId, setSelectedRecipeId] = useState('');
    const [totalQuantity, setTotalQuantity] = useState(item.totalUnshipped || 1);
    const [nakisQuantity, setNakisQuantity] = useState(0);
    const [dikimQuantity, setDikimQuantity] = useState(0);

    // Filter recipes for this product
    const productRecipes = recipes.filter(r => r.product === item.name);

    useEffect(() => {
        if (productRecipes.length === 0 && selectedRecipeId === '') {
            setSelectedRecipeId('no_recipe');
        }
    }, [productRecipes.length, selectedRecipeId]);

    useEffect(() => {
        if (!userData?.companyId || !item.name) return;

        const fetchActiveOrders = async () => {
            setLoading(true);
            try {
                const q = query(collection(db, 'companies', userData.companyId, 'productionOrders'));
                const snapshot = await getDocs(q);
                const orders = snapshot.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(o => o.product === item.name && o.status !== 'completed' && o.status !== 'cancelled' && o.status !== 'Tamamlandı');
                
                setActiveOrders(orders);
            } catch (err) {
                console.error("Error fetching active orders:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchActiveOrders();
    }, [userData, item.name]);

    const handleCreateOrder = async () => {
        if (!selectedRecipeId || totalQuantity <= 0) {
            alert('Lütfen bir reçete seçin ve geçerli bir miktar girin.');
            return;
        }

        const kalan = totalQuantity - nakisQuantity - dikimQuantity;
        if (kalan < 0) {
            alert('Nakış ve Dikim miktarlarının toplamı, toplam miktardan büyük olamaz!');
            return;
        }

        let recipeName, product, recipeId, baseStages;

        if (selectedRecipeId === 'no_recipe') {
            recipeName = 'Reçetesiz';
            product = item.name;
            recipeId = null;
            baseStages = [
                { id: `k`, name: 'Kesim', sequence: 1, status: 'pending', inputConsumed: [], outputProduced: { product: item.name, quantity: 0, unit: 'adet' } },
                { id: `n`, name: 'Nakış', sequence: 2, status: 'pending', inputConsumed: [], outputProduced: { product: item.name, quantity: 0, unit: 'adet' } },
                { id: `d`, name: 'Dikim', sequence: 3, status: 'pending', inputConsumed: [], outputProduced: { product: item.name, quantity: 0, unit: 'adet' } },
                { id: `u`, name: 'Ütü/Paket', sequence: 4, status: 'pending', inputConsumed: [], outputProduced: { product: item.name, quantity: 0, unit: 'adet' } }
            ];
        } else {
            const recipe = recipes.find(r => r.id === selectedRecipeId);
            if (!recipe) return;

            recipeName = recipe.name;
            product = recipe.product;
            recipeId = recipe.id;
            baseStages = recipe.stages ? recipe.stages.map(stage => ({
                ...stage,
                id: `${stage.id}`,
                status: 'pending',
                inputConsumed: (stage.inputMaterials || []).map(m => ({ ...m, quantity: 0 })),
                outputProduced: {
                    product: stage.outputProduct?.name || recipe.product,
                    quantity: 0,
                    unit: stage.outputProduct?.unit || 'adet'
                }
            })) : [];
        }

        setLoading(true);
        try {
            const newOrder = {
                name: `${recipeName} Üretimi`,
                product: product,
                recipeData: recipeId ? [{ recipeId: recipeId, recipeName: recipeName, product: product, quantity: totalQuantity }] : [],
                quantity: totalQuantity,
                priority: 'normal',
                startDate: new Date().toISOString().split('T')[0],
                endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                status: 'inProgress',
                stages: baseStages,
                stageQuantities: {
                    bekleme: kalan,
                    nakis: nakisQuantity,
                    dikim: dikimQuantity,
                    utu: 0,
                    tamamlanan: 0
                },
                createdAt: serverTimestamp(),
                createdBy: userData?.email || 'Bilinmeyen Kullanıcı'
            };

            const docRef = await addDoc(collection(db, 'companies', userData.companyId, 'productionOrders'), newOrder);
            
            setActiveOrders([...activeOrders, { id: docRef.id, ...newOrder, createdAt: new Date() }]);
            alert('Üretim emri başarıyla oluşturuldu!');
            setTotalQuantity(item.totalUnshipped);
            setNakisQuantity(0);
            setDikimQuantity(0);
            setSelectedRecipeId('');
        } catch (error) {
            console.error("Error creating production order:", error);
            alert('Üretim emri oluşturulurken hata: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTransferQuantity = async (orderId, sourceStage, targetStage, availableQty) => {
        if (availableQty <= 0) return;

        const qtyToTransferStr = window.prompt(`Kaç adet aktarmak istiyorsunuz? (Maks: ${availableQty})`, availableQty);
        if (qtyToTransferStr === null) return;

        const qtyToTransfer = Number(qtyToTransferStr);
        if (isNaN(qtyToTransfer) || qtyToTransfer <= 0 || qtyToTransfer > availableQty) {
            alert('Lütfen geçerli bir miktar girin.');
            return;
        }

        setLoading(true);
        try {
            const order = activeOrders.find(o => o.id === orderId);
            const sq = order.stageQuantities || { bekleme: order.quantity, nakis: 0, dikim: 0, utu: 0, tamamlanan: 0 };
            
            const newSq = { ...sq };
            newSq[sourceStage] -= qtyToTransfer;
            newSq[targetStage] += qtyToTransfer;

            let newStatus = order.status;
            if (newSq.tamamlanan === order.quantity) {
                newStatus = 'Tamamlandı';
            }

            const orderRef = doc(db, 'companies', userData.companyId, 'productionOrders', orderId);
            await updateDoc(orderRef, {
                stageQuantities: newSq,
                status: newStatus,
                updatedAt: serverTimestamp()
            });

            // Stoğa giriş yap (Bitir)
            if (targetStage === 'tamamlanan' && item.productId) {
                const productRef = doc(db, 'companies', userData.companyId, 'products', item.productId);
                const productSnap = await getDoc(productRef);
                
                if (productSnap.exists()) {
                    const productData = productSnap.data();
                    let warehouseUpdate = {};
                    let warehouseName = 'Varsayılan Depo';
                    
                    if (productData.warehouseQuantities && Object.keys(productData.warehouseQuantities).length > 0) {
                        const defaultWarehouseId = Object.keys(productData.warehouseQuantities)[0];
                        warehouseUpdate = {
                            [`warehouseQuantities.${defaultWarehouseId}`]: increment(qtyToTransfer)
                        };
                    }
                    
                    await updateDoc(productRef, {
                        quantity: increment(qtyToTransfer),
                        ...warehouseUpdate
                    });
                    
                    const movementRef = collection(db, 'companies', userData.companyId, 'products', item.productId, 'movements');
                    await addDoc(movementRef, {
                        date: serverTimestamp(),
                        type: 'Üretimden Giriş',
                        quantity: `+${qtyToTransfer} ${productData.unit || 'Adet'}`,
                        description: `${order.name} (Emir: #${order.id.substring(0,5).toUpperCase()}) tamamlanarak stoğa girdi.`,
                        warehouseName: warehouseName
                    });
                }
            }

            setActiveOrders(activeOrders.map(o => {
                if (o.id === orderId) return { ...o, stageQuantities: newSq, status: newStatus };
                return o;
            }).filter(o => o.status !== 'Tamamlandı'));

        } catch (error) {
            console.error("Error transferring quantity:", error);
            alert('Miktar aktarılırken hata: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.panel}>
                <h4 style={styles.title}>Hızlı Üretim Emri Oluştur</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <select 
                        style={{...styles.select, width: '100%'}} 
                        value={selectedRecipeId} 
                        onChange={(e) => setSelectedRecipeId(e.target.value)}
                        disabled={loading}
                    >
                        <option value="">Reçete Seçiniz...</option>
                        {productRecipes.map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                        <option value="no_recipe">Reçetesiz Üretim (Standart Aşamalar)</option>
                    </select>
                    {productRecipes.length === 0 && selectedRecipeId !== 'no_recipe' && (
                        <span style={{ fontSize: '12px', color: '#ef4444' }}>Bu ürün için reçete bulunamadı.</span>
                    )}

                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Toplam Üretim</label>
                            <input 
                                type="number" 
                                style={styles.input} 
                                value={totalQuantity} 
                                onChange={(e) => setTotalQuantity(Number(e.target.value))}
                                min="1"
                                disabled={loading}
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={{...styles.label, color: '#8b5cf6'}}>Nakışa Verilen</label>
                            <input 
                                type="number" 
                                style={styles.input} 
                                value={nakisQuantity} 
                                onChange={(e) => setNakisQuantity(Number(e.target.value))}
                                min="0"
                                disabled={loading}
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={{...styles.label, color: '#f59e0b'}}>Dikimde Olan</label>
                            <input 
                                type="number" 
                                style={styles.input} 
                                value={dikimQuantity} 
                                onChange={(e) => setDikimQuantity(Number(e.target.value))}
                                min="0"
                                disabled={loading}
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={{...styles.label, color: '#64748b'}}>Kalan (Başlangıç)</label>
                            <div style={{...styles.input, background: '#f1f5f9', display: 'flex', alignItems: 'center', color: (totalQuantity - nakisQuantity - dikimQuantity) < 0 ? '#ef4444' : '#334155', fontWeight: '600'}}>
                                {totalQuantity - nakisQuantity - dikimQuantity}
                            </div>
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                        <button 
                            style={styles.button} 
                            onClick={handleCreateOrder}
                            disabled={loading || !selectedRecipeId || (totalQuantity - nakisQuantity - dikimQuantity) < 0}
                        >
                            {loading ? 'İşleniyor...' : 'Oluştur'}
                        </button>
                    </div>
                </div>
            </div>

            <div style={styles.panel}>
                <h4 style={styles.title}>Aktif Üretim Emirleri ({activeOrders.length})</h4>
                {activeOrders.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#64748b' }}>Bu ürün için aktif üretim bulunmuyor.</div>
                ) : (
                    <div style={styles.ordersList}>
                        {activeOrders.map(order => {
                            const sq = order.stageQuantities || { bekleme: order.quantity, nakis: 0, dikim: 0, utu: 0, tamamlanan: 0 };
                            return (
                                <div key={order.id} style={styles.orderCard}>
                                    <div style={styles.orderHeader}>
                                        <span style={{ fontWeight: '600' }}>{order.name} <span style={{fontSize:'12px',color:'#94a3b8'}}>#{order.id.substring(0,5).toUpperCase()}</span></span>
                                        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 'bold' }}>Top: {order.quantity}</span>
                                    </div>
                                    <div style={styles.stagesList}>
                                        
                                        <div style={styles.kanbanRow}>
                                            <div style={styles.kanbanLabel}>Bekleme (Kesim): <strong>{sq.bekleme || 0}</strong></div>
                                            <button 
                                                style={styles.actionBtn} 
                                                disabled={loading || !sq.bekleme}
                                                onClick={() => handleTransferQuantity(order.id, 'bekleme', 'nakis', sq.bekleme)}
                                            >
                                                Nakışa Ver ➜
                                            </button>
                                        </div>

                                        <div style={styles.kanbanRow}>
                                            <div style={{...styles.kanbanLabel, color: '#8b5cf6'}}>Nakışta: <strong>{sq.nakis || 0}</strong></div>
                                            <button 
                                                style={styles.actionBtn} 
                                                disabled={loading || !sq.nakis}
                                                onClick={() => handleTransferQuantity(order.id, 'nakis', 'dikim', sq.nakis)}
                                            >
                                                Dikime Ver ➜
                                            </button>
                                        </div>

                                        <div style={styles.kanbanRow}>
                                            <div style={{...styles.kanbanLabel, color: '#f59e0b'}}>Dikimde: <strong>{sq.dikim || 0}</strong></div>
                                            <button 
                                                style={styles.actionBtn} 
                                                disabled={loading || !sq.dikim}
                                                onClick={() => handleTransferQuantity(order.id, 'dikim', 'utu', sq.dikim)}
                                            >
                                                Ütü/Pakete Ver ➜
                                            </button>
                                        </div>

                                        <div style={styles.kanbanRow}>
                                            <div style={{...styles.kanbanLabel, color: '#10b981'}}>Ütü/Pakette: <strong>{sq.utu || 0}</strong></div>
                                            <button 
                                                style={{...styles.actionBtn, background: '#10b981', color: 'white', border: 'none'}} 
                                                disabled={loading || !sq.utu}
                                                onClick={() => handleTransferQuantity(order.id, 'utu', 'tamamlanan', sq.utu)}
                                            >
                                                Bitir (Stoğa Gir) ➜
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        gap: '24px',
        padding: '16px 32px',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        alignItems: 'flex-start'
    },
    panel: {
        flex: 1,
        background: 'white',
        padding: '16px',
        borderRadius: '12px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0'
    },
    title: {
        margin: '0 0 12px 0',
        fontSize: '14px',
        fontWeight: '600',
        color: '#0f172a'
    },
    createRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    label: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#475569'
    },
    select: {
        padding: '8px 12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '13px',
        outline: 'none'
    },
    input: {
        width: '100px',
        height: '34px',
        padding: '8px 12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '13px',
        outline: 'none',
        boxSizing: 'border-box'
    },
    button: {
        padding: '8px 16px',
        background: '#06C167',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    ordersList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxHeight: '350px',
        overflowY: 'auto',
        paddingRight: '8px'
    },
    orderCard: {
        padding: '12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: '#f8fafc'
    },
    orderHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px',
        fontSize: '14px'
    },
    stagesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
    },
    kanbanRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 12px',
        background: 'white',
        borderRadius: '6px',
        border: '1px solid #f1f5f9'
    },
    kanbanLabel: {
        fontSize: '13px',
        color: '#475569'
    },
    actionBtn: {
        padding: '4px 10px',
        background: '#eff6ff',
        color: '#3b82f6',
        border: '1px solid #bfdbfe',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '600',
        cursor: 'pointer'
    }
};

export default UnshippedProductionActions;
